// ============================================================================
// Breadcrumb Component
// Feature: 035-app-shell-enhancement
// Created: 2026-01-21
// Description: Reusable breadcrumb navigation component with collapsible
//              support for long paths, keyboard navigation, and integration
//              with existing generateBreadcrumbs utility.
// User Story: US3 (Breadcrumb Navigation)
// ============================================================================

import React from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';

import { Breadcrumb as BreadcrumbType } from '../../types/shell';

interface BreadcrumbProps {
  /** Array of breadcrumb segments */
  breadcrumbs: BreadcrumbType[];
  /** Maximum number of segments to show before collapsing */
  maxSegments?: number;
  /** Additional CSS classes */
  className?: string;
  /** Custom separator element */
  separator?: React.ReactNode;
}

/**
 * Breadcrumb navigation component
 * 
 * Features:
 * - Automatic collapsing for paths with >5 segments (configurable)
 * - Keyboard navigation support
 * - Clickable navigation links with hover states
 * - Accessible markup with proper ARIA labels
 * - Custom separator support
 * - Responsive design for mobile devices
 */
export const Breadcrumb: React.FC<BreadcrumbProps> = ({
  breadcrumbs = [],
  maxSegments = 5,
  className,
  separator = <ChevronRightIcon />,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  // Determine if we need to collapse breadcrumbs
  const shouldCollapse = breadcrumbs.length > maxSegments && !isExpanded;
  
  const displayBreadcrumbs = shouldCollapse
    ? [
        ...breadcrumbs.slice(0, 2), // First 2 segments
        { 
          label: '...', 
          path: '#expand', 
          isActive: false,
          isCollapsedIndicator: true 
        } as BreadcrumbType & { isCollapsedIndicator?: boolean },
        ...breadcrumbs.slice(-2), // Last 2 segments
      ]
    : breadcrumbs;

  // Handle expand/collapse toggle
  const handleExpandToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsExpanded(!isExpanded);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent, breadcrumb: BreadcrumbType) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (breadcrumb.path === '#expand') {
        setIsExpanded(!isExpanded);
      }
    }
  };

  if (!breadcrumbs.length) {
    return null;
  }

  return (
    <nav
      className={clsx(
        'flex items-center space-x-1 text-sm text-gray-600 dark:text-gray-400',
        'bg-gray-50 dark:bg-gray-900 px-4 py-2 border-b dark:border-gray-700',
        className
      )}
      aria-label="Breadcrumb navigation"
    >
      <ol className="flex items-center space-x-1 min-w-0">
        {displayBreadcrumbs.map((breadcrumb, index) => (
          <li
            key={`${breadcrumb.path}-${index}`}
            className="flex items-center space-x-1 min-w-0"
          >
            {index > 0 && (
              <span className="text-gray-400 dark:text-gray-500 flex-shrink-0" aria-hidden="true">
                {separator}
              </span>
            )}
            
            {breadcrumb.isActive ? (
              // Current/active page - not clickable
              <span
                className="font-medium text-gray-900 dark:text-gray-100 truncate max-w-xs"
                aria-current="page"
              >
                {breadcrumb.label}
              </span>
            ) : breadcrumb.path === '#expand' ? (
              // Collapse/expand toggle
              <button
                onClick={handleExpandToggle}
                onKeyDown={(e) => handleKeyDown(e, breadcrumb)}
                className={clsx(
                  'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded',
                  'px-2 py-1 transition-colors duration-150'
                )}
                aria-label={isExpanded ? 'Collapse breadcrumbs' : 'Show all breadcrumb segments'}
                type="button"
              >
                <span className="font-medium">...</span>
              </button>
            ) : (
              // Regular navigation link
              <Link
                to={breadcrumb.path}
                className={clsx(
                  'text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500 rounded',
                  'truncate max-w-xs transition-colors duration-150',
                  'px-1 py-0.5'
                )}
                title={breadcrumb.label}
              >
                {breadcrumb.label}
              </Link>
            )}
          </li>
        ))}
      </ol>

      {/* Mobile: Show only current page on very small screens */}
      <div className="flex md:hidden ml-auto">
        {breadcrumbs.length > 1 && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {breadcrumbs.findIndex(b => b.isActive) + 1} of {breadcrumbs.length}
          </span>
        )}
      </div>
    </nav>
  );
};

// Default chevron separator icon
const ChevronRightIcon: React.FC = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 5l7 7-7 7"
    />
  </svg>
);

export default Breadcrumb;